// ===========================================
// Lessons React Query Hooks
// ===========================================
// Custom hooks for lesson management API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { PatternClampWarning } from '../api/lessons.api';
import {
  lessonsApi,
  LessonFilters,
  CreateLessonInput,
  BulkCreateLessonsInput,
  UpdateLessonInput,
  ConflictCheckInput,
  RescheduleInput,
  SingleOccurrenceConflictCheckInput,
  SingleOccurrenceRescheduleInput,
  CancelOccurrenceInput,
  ChangeLessonRoomInput,
  LessonExceptionData,
  LessonOccurrence,
  LessonOccurrencesParams,
  AvailableTeacher,
  AssignReplacementTeacherInput,
  AssignReplacementAllWeeksInput,
  ReplacementCoverage,
} from '../api/lessons.api';
import { apiClient } from '../services/api';
import { calendarKeys, hybridBookingKeys } from './useHybridBooking';
import { attendanceKeys } from './useAttendance';
import { notesKeys } from './useNotes';
import { invoiceKeys } from './useInvoices';
import { dashboardKeys } from './useDashboard';
import { ENQUIRY_KEY_ROOT, SETTLING_IN_KEY_ROOT } from './enquiryKeyRoots';

/**
 * A student's FIRST-EVER enrolment opens an onboarding check-in on the server, which moves the
 * enquiry and the dashboard counts. Refresh them after any enrolment write (F-L1).
 */
function invalidateOnboardingReaders(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: SETTLING_IN_KEY_ROOT });
  queryClient.invalidateQueries({ queryKey: ENQUIRY_KEY_ROOT });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.admin.all });
}

/**
 * The invoice week picker's data (billable context, 5-minute stale time) and its previews
 * carry the class window and the stored start week. Refresh them after either changes, or a
 * Create / Amend dialog opened before the change seeds and bounds by the old weeks.
 */
function invalidateInvoiceWeekPicker(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'billable-context'] });
  queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'week-preview'] });
}

/**
 * A lesson write can change its stored class weeks (`classWeeks`, a band's rehearsals) and so
 * the weeks a band derives. Both are cached per (lesson, term) by `useBandTerm` /
 * `useBandWeeksPreview`: left alone, reopening the class within the 5-minute staleTime loaded
 * the OLD row into the form, and the next save of any field wrote it back to every term
 * (/qa class weeks Phase 7 H1). Prefix match: every lesson, every term.
 */
function invalidateClassWeeks(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: [...lessonKeys.all, 'bandTerm'] });
  queryClient.invalidateQueries({ queryKey: [...lessonKeys.all, 'bandWeeks'] });
}

// ===========================================
// QUERY KEYS
// ===========================================

export const lessonKeys = {
  all: ['lessons'] as const,
  lists: () => [...lessonKeys.all, 'list'] as const,
  list: (filters?: LessonFilters) => [...lessonKeys.lists(), filters] as const,
  details: () => [...lessonKeys.all, 'detail'] as const,
  detail: (id: string) => [...lessonKeys.details(), id] as const,
  enrollments: (lessonId: string) =>
    [...lessonKeys.all, 'enrollments', lessonId] as const,
  classMembers: (lessonId: string) =>
    [...lessonKeys.all, 'classMembers', lessonId] as const,
  capacity: (lessonId: string) =>
    [...lessonKeys.all, 'capacity', lessonId] as const,
  exceptions: (lessonId: string) =>
    [...lessonKeys.all, 'exceptions', lessonId] as const,
  /**
   * Dated occurrences. Deliberately nested UNDER `lists()` so that every
   * existing `invalidateQueries({ queryKey: lessonKeys.lists() })` — there are
   * 13 of them, covering reschedule, cancel, delete, room change and more —
   * prefix-matches and refreshes occurrences too.
   *
   * A sibling key (`[...all, 'occurrences']`) would NOT be matched by those,
   * so an admin reschedule would leave the teacher's page showing stale dates
   * until the 5-minute staleTime expired — the very bug this work fixes,
   * arriving through a different door. Nesting means a future mutation can't
   * forget to invalidate this.
   */
  occurrences: (params: LessonOccurrencesParams) =>
    [...lessonKeys.lists(), 'occurrences', params] as const,
  studentEnrollments: (studentId: string) =>
    [...lessonKeys.all, 'studentEnrollments', studentId] as const,
  availableReplacementTeachers: (lessonId: string, weekNumbers?: number[]) =>
    [...lessonKeys.all, 'availableReplacementTeachers', lessonId, weekNumbers ?? []] as const,
  replacementCoverage: (lessonId: string) =>
    [...lessonKeys.all, 'replacementCoverage', lessonId] as const,
  bandLessons: (termId?: string) =>
    [...lessonKeys.all, 'band', termId] as const,
  bandWeeksPreview: (bandLessonId: string, termId: string) =>
    [...lessonKeys.all, 'bandWeeks', bandLessonId, termId] as const,
  bandTerm: (lessonId: string, termId: string) =>
    [...lessonKeys.all, 'bandTerm', lessonId, termId] as const,
};

// ===========================================
// LESSON QUERIES
// ===========================================

/**
 * Get all lessons with optional filters
 * Note: lessonsApi already extracts .data from ApiResponse wrapper
 */
export function useLessons(filters?: LessonFilters) {
  return useQuery({
    queryKey: lessonKeys.list(filters),
    queryFn: () => lessonsApi.getAll(filters),
  });
}

/**
 * Get lessons for a specific teacher
 * Convenience wrapper around useLessons with teacherId filter
 */
export function useLessonsByTeacher(teacherId: string) {
  return useQuery({
    queryKey: lessonKeys.list({ teacherId }),
    queryFn: () => lessonsApi.getAll({ teacherId }),
    enabled: !!teacherId,
  });
}

/**
 * Get a single lesson by ID
 * Note: lessonsApi already extracts .data from ApiResponse wrapper
 */
export function useLesson(id: string) {
  return useQuery({
    queryKey: lessonKeys.detail(id),
    queryFn: () => lessonsApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get all active band lessons for the school.
 * Pass enabled=false to skip the query (e.g., when combo is GROUP_INDIVIDUAL).
 */
export function useBandLessons(termId?: string, enabled = true) {
  return useQuery({
    queryKey: lessonKeys.bandLessons(termId),
    queryFn: () => lessonsApi.getBandLessons(termId),
    enabled,
  });
}

/**
 * Preview which weeks a band lesson falls on within a term.
 * Only enabled when both bandLessonId and termId are non-empty.
 */
export function useBandWeeksPreview(bandLessonId: string, termId: string) {
  return useQuery({
    queryKey: lessonKeys.bandWeeksPreview(bandLessonId, termId),
    queryFn: () => lessonsApi.getBandWeeksPreview(bandLessonId, termId),
    enabled: !!bandLessonId && !!termId,
  });
}

/**
 * Get enrollments for a lesson
 * Note: lessonsApi already extracts .data from ApiResponse wrapper
 */
export function useLessonEnrollments(lessonId: string) {
  return useQuery({
    queryKey: lessonKeys.enrollments(lessonId),
    queryFn: () => lessonsApi.getEnrollments(lessonId),
    enabled: !!lessonId,
  });
}

/**
 * Get messageable class members for a lesson (parents + students with accounts).
 * Only fetched when the modal opens (enabled flag).
 */
export function useLessonClassMembers(lessonId: string, enabled: boolean) {
  return useQuery({
    queryKey: lessonKeys.classMembers(lessonId),
    queryFn: () => lessonsApi.getClassMembers(lessonId),
    enabled: !!lessonId && enabled,
  });
}

/**
 * Get available instruments and lesson types for a student
 * Used by parent when requesting extra classes to populate dropdowns
 */
export function useStudentEnrollments(studentId: string | null) {
  return useQuery({
    queryKey: lessonKeys.studentEnrollments(studentId || ''),
    queryFn: async () => {
      const response = await apiClient.get<{
        status: string;
        data: {
          instruments: Array<{ id: string; name: string; teacherId: string | null; teacherName: string | null }>;
          lessonTypes: Array<{ id: string; name: string }>;
        };
      }>(`/bookings/extra/available/${studentId}`);
      // Return the instruments and lesson types from the data
      return {
        instruments: response.data.instruments,
        lessonTypes: response.data.lessonTypes,
      };
    },
    enabled: !!studentId,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Get enrollment capacity for a lesson
 * Note: lessonsApi already extracts .data from ApiResponse wrapper
 */
export function useLessonCapacity(lessonId: string) {
  return useQuery({
    queryKey: lessonKeys.capacity(lessonId),
    queryFn: () => lessonsApi.checkCapacity(lessonId),
    enabled: !!lessonId,
  });
}

// ===========================================
// LESSON MUTATIONS
// ===========================================

/**
 * Surface the server's pattern-week clamp warnings (D3 "tell"): one warning
 * toast per warning, after the success toast, held long enough to read. The
 * page discards mutation results and closes its dialog, so the hook is the one
 * place every lesson write passes through. Tolerates a missing array so an
 * older server (or a test mock returning a bare lesson) never throws here.
 */
export function notifyPatternWarnings(
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
  warnings: PatternClampWarning[] | undefined
): void {
  for (const w of warnings ?? []) {
    enqueueSnackbar(w.message, { variant: 'warning', autoHideDuration: 12000 });
  }
}

/**
 * Create a new lesson
 */
export function useCreateLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateLessonInput) => lessonsApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      invalidateClassWeeks(queryClient);
      // A new lesson may be a hybrid pattern → refresh the dashboard status card
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      enqueueSnackbar('Lesson created successfully', { variant: 'success' });
      notifyPatternWarnings(enqueueSnackbar, result?.warnings);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create lesson';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Bulk create lessons for multiple terms
 */
export function useBulkCreateLessons() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: BulkCreateLessonsInput) => lessonsApi.bulkCreate(data),
    onSuccess: (result) => {
      const lessons = result.data;
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      invalidateClassWeeks(queryClient);
      // Bulk-created lessons may include hybrid patterns → refresh status card
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      enqueueSnackbar(
        `${lessons.length} lesson${lessons.length === 1 ? '' : 's'} created successfully`,
        { variant: 'success' }
      );
      notifyPatternWarnings(enqueueSnackbar, result.warnings);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create lessons';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update a lesson
 */
export function useUpdateLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLessonInput }) =>
      lessonsApi.update(id, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.capacity(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.enrollments(variables.id),
      });
      // Editing a lesson can change its hybrid pattern/term → refresh status card
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      // Its class weeks (fortnightly, a band's rehearsals) may have changed (/qa Phase 7 H1).
      invalidateClassWeeks(queryClient);
      // A new first / last week changes what the invoice week picker offers (/qa Phase 3).
      invalidateInvoiceWeekPicker(queryClient);
      enqueueSnackbar('Lesson updated successfully', { variant: 'success' });
      notifyPatternWarnings(enqueueSnackbar, result?.warnings);
    },
    onError: (
      error: Error & { response?: { data?: { message?: string; details?: { code?: string } } } }
    ) => {
      // A refused window change is answered by WindowHasDataDialog (the page catches it) — a
      // toast on top would say the same sentence twice.
      if (error.response?.data?.details?.code === 'WINDOW_HAS_DATA') return;
      const message = error.response?.data?.message || 'Failed to update lesson';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete a lesson (soft delete by default)
 */
export function useDeleteLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, hardDelete = false, force = false }: { id: string; hardDelete?: boolean; force?: boolean }) =>
      lessonsApi.delete(id, hardDelete, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      invalidateClassWeeks(queryClient);
      // Deleting a hybrid lesson should drop it from the status card
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      enqueueSnackbar('Lesson deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete lesson';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// ENROLLMENT MUTATIONS
// ===========================================

/**
 * Enroll a student in a lesson
 */
export function useEnrollStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      studentId,
      enrollInAllTerms,
      startWeek,
    }: {
      lessonId: string;
      studentId: string;
      enrollInAllTerms?: boolean;
      startWeek?: number | null;
    }) => lessonsApi.enrollStudent(lessonId, studentId, enrollInAllTerms, startWeek),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.enrollments(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.capacity(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // The invoice dialogs list enrolments (Create Invoice's prefill matches one by id).
      invalidateInvoiceWeekPicker(queryClient);
      invalidateOnboardingReaders(queryClient);
      // Also invalidate student enrollments query
      queryClient.invalidateQueries({
        queryKey: ['users', 'students', variables.studentId, 'enrollments'],
      });
      const message = variables.enrollInAllTerms
        ? 'Student enrolled in all terms successfully'
        : 'Student enrolled successfully';
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to enroll student';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Bulk enroll students in a lesson
 */
export function useBulkEnrollStudents() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      studentIds,
      enrollInAllTerms,
      startWeek,
    }: {
      lessonId: string;
      studentIds: string[];
      enrollInAllTerms?: boolean;
      startWeek?: number | null;
    }) => lessonsApi.bulkEnroll(lessonId, studentIds, enrollInAllTerms, startWeek),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.enrollments(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.capacity(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // The invoice dialogs list enrolments (Create Invoice's prefill matches one by id).
      invalidateInvoiceWeekPicker(queryClient);
      invalidateOnboardingReaders(queryClient);
      const message = variables.enrollInAllTerms
        ? `${variables.studentIds.length} students enrolled in all terms successfully`
        : `${variables.studentIds.length} students enrolled successfully`;
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to enroll students';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Change the week an enrolled student joins (null = the class's own start).
 * Invalidates the same keys as enrol/unenrol. A 409 WINDOW_HAS_DATA is NOT toasted:
 * the caller shows WindowHasDataDialog, so `onError` stays silent for it.
 * The result carries `invoicesToAmend` (sent invoices that MAY need amending) for the caller
 * to hand to InvoicesToAmendDialog. `silent: true` skips BOTH toasts — for the invoice
 * dialog's write-back, which reports the outcome in its own sentence.
 */
export function useUpdateEnrollment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      studentId,
      startWeek,
    }: {
      lessonId: string;
      studentId: string;
      startWeek: number | null;
      silent?: boolean;
    }) => lessonsApi.updateEnrollment(lessonId, studentId, startWeek),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.enrollments(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.capacity(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ['users', 'students', variables.studentId, 'enrollments'],
      });
      // The picker seeds the stored start week: an open Create / Amend dialog must see the new one.
      invalidateInvoiceWeekPicker(queryClient);
      // The start week also decides who is on each roll, which clinic weeks the child can book
      // (pre-booking chips, the bookings-open grid) and which sessions calendars show
      // (whole-feature /qa L2).
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      // The bookings-open grid's stats live under a DIFFERENT root ('hybridBooking', no s) —
      // see useHybridBooking's bookingOpenStats / termBookingOpenStats (final /qa F1).
      queryClient.invalidateQueries({ queryKey: ['hybridBooking'] });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: [...attendanceKeys.all, 'students'] });
      queryClient.invalidateQueries({ queryKey: [...attendanceKeys.all, 'roster-status'] });
      if (!variables.silent) enqueueSnackbar('Starting week updated', { variant: 'success' });
    },
    onError: (
      error: Error & { response?: { data?: { message?: string; details?: { code?: string } } } },
      variables
    ) => {
      if (variables.silent) return;
      if (error.response?.data?.details?.code === 'WINDOW_HAS_DATA') return;
      const message = error.response?.data?.message || 'Failed to update the starting week';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Unenroll a student from a lesson
 */
export function useUnenrollStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      studentId,
    }: {
      lessonId: string;
      studentId: string;
    }) => lessonsApi.unenrollStudent(lessonId, studentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.enrollments(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.capacity(variables.lessonId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // The invoice dialogs list enrolments (Create Invoice's prefill matches one by id).
      invalidateInvoiceWeekPicker(queryClient);
      // Also invalidate student enrollments query
      queryClient.invalidateQueries({
        queryKey: ['users', 'students', variables.studentId, 'enrollments'],
      });
      enqueueSnackbar('Student removed from lesson', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to remove student';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// AVAILABILITY CHECK HOOKS
// ===========================================

/**
 * Check room availability
 */
export function useCheckRoomAvailability() {
  return useMutation({
    mutationFn: ({
      roomId,
      dayOfWeek,
      startTime,
      endTime,
      excludeLessonId,
    }: {
      roomId: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      excludeLessonId?: string;
    }) =>
      lessonsApi.checkRoomAvailability(
        roomId,
        dayOfWeek,
        startTime,
        endTime,
        excludeLessonId
      ),
  });
}

/**
 * Check teacher availability
 */
export function useCheckTeacherAvailability() {
  return useMutation({
    mutationFn: ({
      teacherId,
      dayOfWeek,
      startTime,
      endTime,
      excludeLessonId,
    }: {
      teacherId: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      excludeLessonId?: string;
    }) =>
      lessonsApi.checkTeacherAvailability(
        teacherId,
        dayOfWeek,
        startTime,
        endTime,
        excludeLessonId
      ),
  });
}

// ===========================================
// RESCHEDULE HOOKS
// ===========================================

/**
 * Check for conflicts before rescheduling a lesson
 */
export function useCheckRescheduleConflicts() {
  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: ConflictCheckInput;
    }) => lessonsApi.checkRescheduleConflicts(lessonId, input),
  });
}

/**
 * Get related lessons across terms (same name, teacher, room, time)
 * Used for "apply to all terms" reschedule option
 */
export function useRelatedLessons(lessonId: string | null) {
  return useQuery({
    queryKey: [...lessonKeys.detail(lessonId || ''), 'related'],
    queryFn: () => lessonsApi.getRelatedLessons(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Reschedule a lesson to a new time slot
 */
export function useRescheduleLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: RescheduleInput;
    }) => lessonsApi.reschedule(lessonId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(variables.lessonId),
      });
      // Also invalidate calendar events
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      const message = variables.input.applyToAllTerms
        ? 'Lesson rescheduled across all terms'
        : 'Lesson rescheduled successfully';
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reschedule lesson';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// CHANGE LOCATION (ROOM) HOOKS
// ===========================================

/**
 * Check whether a target room is free for an "all classes" location change.
 */
export function useCheckChangeRoomConflicts() {
  return useMutation({
    mutationFn: ({
      lessonId,
      params,
    }: {
      lessonId: string;
      params: { roomId: string; applyToAllTerms?: boolean };
    }) => lessonsApi.checkChangeRoomConflicts(lessonId, params),
  });
}

/**
 * Change a recurring lesson's room ("all classes" scope).
 */
export function useChangeLessonRoom() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: ChangeLessonRoomInput;
    }) => lessonsApi.changeRoom(lessonId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(variables.lessonId) });
      queryClient.invalidateQueries({ queryKey: lessonKeys.exceptions(variables.lessonId) });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      const message = variables.input.applyToAllTerms
        ? 'Location changed across all terms'
        : 'Location changed successfully';
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to change location';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// SINGLE-OCCURRENCE HOOKS
// ===========================================

/**
 * Check for conflicts before rescheduling a single occurrence
 */
export function useCheckSingleOccurrenceConflicts() {
  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: SingleOccurrenceConflictCheckInput;
    }) => lessonsApi.checkSingleOccurrenceConflicts(lessonId, input),
  });
}

/**
 * Reschedule a single occurrence of a recurring lesson
 */
export function useRescheduleSingleOccurrence() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: SingleOccurrenceRescheduleInput;
    }) => lessonsApi.rescheduleSingleOccurrence(lessonId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // Refresh the lesson detail + its exceptions list so a reschedule done from
      // the lesson detail page reflects immediately (the new RESCHEDULED exception).
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(variables.lessonId) });
      queryClient.invalidateQueries({ queryKey: lessonKeys.exceptions(variables.lessonId) });
      // The backend MOVES Attendance rows to the new date (carryAttendanceToNewDate)
      // and note completion is keyed by date, so both caches are stale the instant
      // this succeeds. Without these the row jumps to its new date but its badge
      // still reads "not marked" for attendance that was just carried over.
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      enqueueSnackbar('Class occurrence rescheduled successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reschedule occurrence';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Cancel a single occurrence of a recurring lesson
 */
export function useCancelSingleOccurrence() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      input,
    }: {
      lessonId: string;
      input: CancelOccurrenceInput;
    }) => lessonsApi.cancelSingleOccurrence(lessonId, input),
    onSuccess: (_data, variables) => {
      // Invalidate and immediately refetch calendar queries
      queryClient.invalidateQueries({ queryKey: ['calendar'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists(), refetchType: 'all' });
      // The class detail page's "cancelled classes" section and the Cancel Class
      // dialog's week picker both read the exceptions query — without this they
      // kept showing the pre-cancellation list until a full reload (staging
      // walkthrough, 2026-09-06). Reschedule/restore already did this.
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(variables.lessonId) });
      queryClient.invalidateQueries({ queryKey: lessonKeys.exceptions(variables.lessonId) });
      // Cancelling can write a synthetic EXCUSED row (the make-up credit anchor)
      // and flips the occurrence to struck-through/no-buttons, so the attendance
      // and notes caches are stale here too.
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      enqueueSnackbar('Class occurrence cancelled successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to cancel occurrence';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// EXCEPTION MANAGEMENT
// ===========================================

/**
 * Dated lesson OCCURRENCES with LessonException applied.
 *
 * Use this — not `useLessons` — anywhere a real session date matters (teacher
 * task lists, attendance marking). `useLessons` returns recurring TEMPLATES, so
 * a caller deriving a date from `dayOfWeek` cannot see that an occurrence was
 * rescheduled and will read/write attendance against the wrong date.
 */
export function useLessonOccurrences(params: LessonOccurrencesParams | undefined) {
  return useQuery<LessonOccurrence[]>({
    queryKey: lessonKeys.occurrences(params ?? { startDate: '', endDate: '' }),
    queryFn: () => lessonsApi.getOccurrences(params!),
    enabled: !!params?.startDate && !!params?.endDate,
  });
}

/**
 * Get all exceptions for a lesson (admin viewing/management).
 * The route needs scheduling.read: a caller without it passes `enabled: false` (#272).
 */
export function useLessonExceptions(lessonId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery<LessonExceptionData[]>({
    queryKey: lessonKeys.exceptions(lessonId || ''),
    queryFn: () => lessonsApi.getExceptions(lessonId!),
    enabled: !!lessonId && (options?.enabled ?? true),
  });
}

/**
 * Delete a lesson exception (restore original occurrence)
 */
export function useDeleteLessonException() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      exceptionId,
    }: {
      lessonId: string;
      exceptionId: string;
    }) => lessonsApi.deleteException(lessonId, exceptionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonKeys.exceptions(variables.lessonId) });
      enqueueSnackbar('Exception removed - original occurrence restored', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to remove exception';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// REPLACEMENT TEACHER HOOKS
// ===========================================

/**
 * Get available replacement teachers for a lesson, filtered by instrument +
 * availability and — when weekNumbers are given — to teachers free on ALL of
 * those weeks. Refetches when the selected weeks change.
 */
export function useAvailableReplacementTeachers(
  lessonId: string | undefined,
  weekNumbers?: number[]
) {
  return useQuery<AvailableTeacher[]>({
    queryKey: lessonKeys.availableReplacementTeachers(lessonId || '', weekNumbers),
    queryFn: () => lessonsApi.getAvailableReplacementTeachers(lessonId!, weekNumbers),
    enabled: !!lessonId,
  });
}

/**
 * Get the current replacement coverage for a lesson (legacy global + per-week).
 * Feeds the Assign/Edit modal and the status card.
 * The route needs scheduling.read: a caller without it passes `enabled: false` (#272).
 */
export function useReplacementCoverage(lessonId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery<ReplacementCoverage>({
    queryKey: lessonKeys.replacementCoverage(lessonId || ''),
    queryFn: () => lessonsApi.getReplacementCoverage(lessonId!),
    enabled: !!lessonId && (options?.enabled ?? true),
  });
}

/** Invalidate every query that reflects a replacement change for a lesson. */
function invalidateReplacementQueries(queryClient: ReturnType<typeof useQueryClient>, lessonId: string) {
  queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
  queryClient.invalidateQueries({ queryKey: lessonKeys.detail(lessonId) });
  queryClient.invalidateQueries({ queryKey: lessonKeys.replacementCoverage(lessonId) });
  // Availability changes after an assign/clear — prefix-match all weeks-parameterised
  // variants for this lesson (the weekNumbers are the trailing key segment).
  queryClient.invalidateQueries({
    queryKey: [...lessonKeys.all, 'availableReplacementTeachers', lessonId],
  });
  // Per-occurrence calendar rendering changes (Phase 8 resolution).
  queryClient.invalidateQueries({ queryKey: calendarKeys.all });
}

/**
 * Assign a replacement teacher for specific weeks. Surfaces any skipped weeks
 * (occurrences already rescheduled/cancelled) in the success toast.
 */
export function useAssignReplacementTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      data,
    }: {
      lessonId: string;
      data: AssignReplacementTeacherInput;
    }) => lessonsApi.assignReplacementTeacher(lessonId, data),
    onSuccess: (result, variables) => {
      invalidateReplacementQueries(queryClient, variables.lessonId);
      if (result.skippedWeeks.length > 0) {
        enqueueSnackbar(
          `Replacement assigned. Week(s) ${result.skippedWeeks.join(', ')} were skipped (already rescheduled or cancelled).`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Replacement teacher assigned successfully', { variant: 'success' });
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to assign replacement teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Assign a replacement teacher to all remaining weeks of the term (shortcut).
 */
export function useAssignReplacementAllWeeks() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      lessonId,
      data,
    }: {
      lessonId: string;
      data: AssignReplacementAllWeeksInput;
    }) => lessonsApi.assignReplacementAllWeeks(lessonId, data),
    onSuccess: (result, variables) => {
      invalidateReplacementQueries(queryClient, variables.lessonId);
      if (result.skippedWeeks.length > 0) {
        enqueueSnackbar(
          `Replacement assigned to remaining weeks. Week(s) ${result.skippedWeeks.join(', ')} were skipped.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Replacement assigned to all remaining weeks', { variant: 'success' });
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to assign replacement teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reinstate the regular teacher for specific weeks (per-week clear).
 */
export function useClearReplacementWeeks() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ lessonId, weekNumbers }: { lessonId: string; weekNumbers: number[] }) =>
      lessonsApi.clearReplacementWeeks(lessonId, weekNumbers),
    onSuccess: (result, variables) => {
      invalidateReplacementQueries(queryClient, variables.lessonId);
      if (result.clearedWeeks.length > 0) {
        enqueueSnackbar(
          `Regular teacher reinstated for week${result.clearedWeeks.length > 1 ? 's' : ''} ${result.clearedWeeks.join(', ')}`,
          { variant: 'success' }
        );
      } else {
        enqueueSnackbar('No replacement found for the selected week(s)', { variant: 'info' });
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reinstate regular teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Clear the replacement teacher entirely (all weeks + legacy global field).
 */
export function useClearReplacementTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (lessonId: string) => lessonsApi.clearReplacementTeacher(lessonId),
    onSuccess: (_data, lessonId) => {
      invalidateReplacementQueries(queryClient, lessonId);
      enqueueSnackbar('Replacement teacher cleared', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to clear replacement teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// BAND TERM HOOKS
// ===========================================

/**
 * Get BandTerm config for a specific band lesson and term.
 * Only enabled when both IDs are non-empty.
 * ⚠️ Always refetched when the dialog mounts: the row SEEDS an edit form, and a cached copy
 * older than the last save would be written back by the next save (/qa class weeks Phase 7
 * H1). The consumer must also wait for `isFetchedAfterMount` — React Query hands back the
 * cached data first, before the refetch lands.
 */
export function useBandTerm(lessonId: string, termId: string) {
  return useQuery({
    queryKey: lessonKeys.bandTerm(lessonId, termId),
    queryFn: () => lessonsApi.getBandTerm(lessonId, termId),
    enabled: !!lessonId && !!termId,
    refetchOnMount: 'always',
  });
}

/**
 * Upsert (create or update) BandTerm config for a band lesson.
 */
export function useUpsertBandTerm(lessonId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: { termId: string; rehearsalWeeks: number[]; patternType: 'EVERY_WEEK' | 'CUSTOM' }) =>
      lessonsApi.upsertBandTerm(lessonId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.bandTerm(lessonId, result.termId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(lessonId) });
      enqueueSnackbar('Rehearsal weeks saved', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to save rehearsal weeks';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete BandTerm config for a band lesson.
 */
export function useDeleteBandTerm(lessonId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (termId: string) => lessonsApi.deleteBandTerm(lessonId, termId),
    onSuccess: (_result, termId) => {
      queryClient.invalidateQueries({
        queryKey: lessonKeys.bandTerm(lessonId, termId),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(lessonId) });
      enqueueSnackbar('Rehearsal config removed', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to remove rehearsal config';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
